<script setup lang="ts">
import { computed, reactive, ref, watch, type Ref } from 'vue';
import CButton from '@/components/shared/CButton';
import CFileInput, { type CFileInputRef } from '@/components/shared/CFileInput';
import CPreview from '@/components/shared/CPreview.vue';
import MaskSelectorModal from './MaskSelectorModal.vue';
import type { MaskDto } from '@/api/typings/mask';
import {
    MASK_UPLOAD_ACCEPT,
    RASTER_MASK_UPLOAD_ACCEPT,
    deriveMaskThumbnailPath,
    maskNameFromPath,
    uploadAndRegisterMask
} from '@/utils/maskUtils';

const props = defineProps<{
    maskPath?: string;
    disabled?: boolean;
    disableSvg?: boolean;
    mixed?: boolean;
}>();

const emit = defineEmits<{
    (event: 'select-mask', mask: MaskDto): void;
    (event: 'remove-mask'): void;
    (event: 'error', message: string): void;
}>();

const model = reactive({
    modalOpen: false,
    uploading: false,
    pickedMask: undefined as MaskDto | undefined
});

const fileInputRef: Ref<CFileInputRef | undefined> = ref();

const hasMask = computed(() => !!props.maskPath || props.mixed);
const accept = computed(() => (props.disableSvg ? RASTER_MASK_UPLOAD_ACCEPT : MASK_UPLOAD_ACCEPT));

const maskThumbnail = computed(() => {
    if (props.mixed || !props.maskPath) return '';
    return model.pickedMask?.thumbnailPath || deriveMaskThumbnailPath(props.maskPath);
});

const maskName = computed(() => {
    if (props.mixed) return 'Mixed';
    if (!props.maskPath) return '';
    return model.pickedMask?.name || maskNameFromPath(props.maskPath);
});

// Masks applied before this feature existed have no generated thumbnail, fall back to the mask itself
const previewSource = ref('');
watch(
    maskThumbnail,
    (thumbnail) => {
        previewSource.value = thumbnail;
    },
    { immediate: true }
);
const onPreviewFailed = () => {
    if (props.maskPath && previewSource.value !== props.maskPath) {
        previewSource.value = props.maskPath;
    }
};

watch(
    () => props.maskPath,
    (maskPath) => {
        if (!maskPath || maskPath !== model.pickedMask?.path) {
            model.pickedMask = undefined;
        }
    }
);

const openModal = () => {
    if (props.disabled) return;
    model.modalOpen = true;
};

const openFileUploader = () => {
    if (props.disabled) return;
    fileInputRef.value?.openFileUploader();
};

const onMaskSelected = (mask: MaskDto) => {
    model.modalOpen = false;
    model.pickedMask = mask;
    emit('select-mask', mask);
};

const onFileUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    model.uploading = true;
    try {
        onMaskSelected(await uploadAndRegisterMask(file));
    } catch (error) {
        emit('error', (error as Error).message || 'The mask could not be uploaded');
    } finally {
        model.uploading = false;
    }
};
</script>

<template>
    <div class="mask-properties">
        <div v-if="hasMask" class="mask-row">
            <c-preview
                v-if="!mixed"
                :src="previewSource"
                :alt="maskName"
                :width="30"
                max-height="30px"
                @preview-failed="onPreviewFailed"
            />
            <span class="mask-name">{{ maskName }}</span>
            <div class="actions ml-auto">
                <c-button
                    :icon="['fas', 'arrows-rotate']"
                    size="icon"
                    title="Choose another shape"
                    :disabled="disabled"
                    @click="openModal"
                />
                <c-button
                    :icon="['fas', 'arrow-up-from-bracket']"
                    size="icon"
                    title="Upload a new shape"
                    :disabled="disabled || model.uploading"
                    @click="openFileUploader"
                />
                <c-button
                    :icon="['far', 'trash-can']"
                    size="icon"
                    title="Remove mask"
                    :disabled="disabled"
                    @click="emit('remove-mask')"
                />
            </div>
        </div>

        <c-button
            v-else
            variant="secondary"
            text="Add Mask"
            :icon="['far', 'plus']"
            :disabled="disabled || model.uploading"
            @click="openModal"
        />

        <c-file-input
            ref="fileInputRef"
            class="d-none"
            :value="[]"
            :accept="accept"
            :display-preview="false"
            @input="onFileUpload"
        />

        <mask-selector-modal
            v-if="model.modalOpen"
            :disable-svg="disableSvg"
            @select="onMaskSelected"
            @cancel="model.modalOpen = false"
            @error="emit('error', $event)"
        />
    </div>
</template>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.mask-properties {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}

.mask-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.5rem;
    background: get-color('purple-lightest');
    border-radius: 0.25rem;
}

.mask-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.actions {
    display: flex;
    gap: 0.25rem;
}
</style>
